import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import UpgradePrompt from "@/components/UpgradePrompt";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">{label}</label>
    {children}
    {hint && <div className="text-xs text-zinc-500 mt-1.5">{hint}</div>}
  </div>
);

export default function NewGiveaway() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    title: "",
    description: "",
    prize_value: 500,
    entry_ratio_dollars: 1,
    entry_ratio_entries: 10,
    start_date: today,
    end_date: in30,
    rules_jurisdiction: "United States",
    sponsor_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const [atLimit, setAtLimit] = useState(false);
  const isPro = ["pro", "agency"].includes(user?.plan);

  useEffect(() => {
    if (isPro) { setAtLimit(false); return; }
    api.get("/giveaways").then((r) => {
      const active = (r.data || []).filter((x) => x.status === "active" && !x.is_demo).length;
      setAtLimit(active >= 1);
    }).catch(() => {});
  }, [isPro]);

  const handle = (k) => (e) => {
    let v;
    if (e.target.type === "number") {
      v = e.target.value === "" ? "" : Number(e.target.value);
    } else {
      v = e.target.value;
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/giveaways", form);
      toast.success("Giveaway created");
      navigate(`/giveaways/${data.giveaway_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-8 py-10">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Create</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2 mb-8">New Giveaway</h1>

        {atLimit ? (
          <UpgradePrompt
            feature="Unlimited giveaways"
            description="Your current plan includes 1 active giveaway. Upgrade to Pro to run unlimited giveaways at once and unlock the full toolkit."
            testid="giveaway-limit-upgrade-prompt"
          />
        ) : (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-md p-8 space-y-6">
          <Field label="Title">
            <input data-testid="giveaway-title-input" className={input} value={form.title} onChange={handle("title")} placeholder="Win a $500 Skincare Bundle" required />
          </Field>
          <Field label="Description">
            <textarea data-testid="giveaway-description-input" className={input + " min-h-[100px]"} value={form.description} onChange={handle("description")} placeholder="Enter to win our complete skincare collection..." />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prize Value ($)">
              <input data-testid="prize-value-input" type="number" min={1} onWheel={(e) => e.currentTarget.blur()} className={input} value={form.prize_value} onChange={handle("prize_value")} />
            </Field>
            <Field label="Sponsor Name">
              <input data-testid="sponsor-name-input" className={input} value={form.sponsor_name} onChange={handle("sponsor_name")} placeholder="Acme Co." />
            </Field>
          </div>

          <div className="border border-zinc-100 bg-zinc-50/50 rounded-md p-4">
            <Field label="Entry Ratio" hint="How many entries customers earn per dollar spent.">
              <div className="flex items-center gap-3 text-sm">
                <span className="text-zinc-500">$</span>
                <input data-testid="ratio-dollars-input" type="number" min={1} step="0.5" onWheel={(e) => e.currentTarget.blur()} className={input + " w-24"} value={form.entry_ratio_dollars} onChange={handle("entry_ratio_dollars")} />
                <span className="text-zinc-500">=</span>
                <input data-testid="ratio-entries-input" type="number" min={1} onWheel={(e) => e.currentTarget.blur()} className={input + " w-24"} value={form.entry_ratio_entries} onChange={handle("entry_ratio_entries")} />
                <span className="text-zinc-500">entries</span>
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Start Date">
              <input data-testid="start-date-input" type="date" className={input} value={form.start_date} onChange={handle("start_date")} />
            </Field>
            <Field label="End Date">
              <input data-testid="end-date-input" type="date" className={input} value={form.end_date} onChange={handle("end_date")} />
            </Field>
          </div>
          <Field label="Jurisdiction" hint="Used in official rules">
            <input data-testid="jurisdiction-input" className={input} value={form.rules_jurisdiction} onChange={handle("rules_jurisdiction")} />
          </Field>

          <div className="flex items-center gap-3 pt-4 border-t border-zinc-100">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-giveaway-button"
              className="bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create giveaway"}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="text-sm text-zinc-600 hover:text-zinc-950">Cancel</button>
          </div>
        </form>
        )}
      </div>
    </Layout>
  );
}
